import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PageHeader from '../../../PageHeader/PageHeader';
import { Box } from 'rizzui';
import AppButton from '../../../components/AppButton/AppButton';
import AppInput from '../../../components/AppInput/AppInput';
import AppSelect from '../../../components/AppSelect/AppSelect';
import AppSwitch from '../../../components/Switch/AppSwitch';
import { HitApi } from '../../../Api/ApiHit';
import { setApiJson, setApiErrorJson } from '../../../redux/Actions/ApiAction';
import { BillboardValidation } from '../../../validationscheema/BillBoardValidation';
import { addMedia, searchMedia, updateMedia, searchDevice } from '../../../constant/Constant';
import FileuploadComp from '../../../components/SingleFileUpload/FileuploadComp';
import { getSelectedUnit } from '../../../storage/Storage';

function AddBillBoard() {
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const dispatch = useDispatch();
    const [isEditMode, setIsEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [devices, setDevices] = useState([]);
    const [deviceOptions, setDeviceOptions] = useState([]);

    const url = window.location.pathname;
    const urlId = url.split('/')[3];

    // Media type options
    const typeOptions = [
        { value: 'image', label: 'Image' },
        { value: 'video', label: 'Video' },
        { value: 'gif', label: 'GIF' }
    ];

    useEffect(() => {
        // Clear any existing errors first
        dispatch(setApiErrorJson({}));

        // Fetch devices first
        fetchDevices();

        // Initialize form with default values
        const initialData = {
            duration: "",
            type: "",
            deviceId: "",
            source: "",
            isActive: false
        };

        // Set initial form data in Redux
        dispatch(setApiJson(initialData));

        // Load data if in edit mode
        if (urlId) {
            setIsEditMode(true);
            loadData();
        }
    }, [dispatch, urlId]);

    const fetchDevices = () => {
        const json = {
            page: 1,
            limit: 100,
            search: {
                unitIds: getSelectedUnit()?._id
            }
        };

        HitApi(json, searchDevice).then((res) => {
            if (res?.statusCode === 200) {
                const deviceData = Array.isArray(res?.data?.docs) ? res?.data?.docs : [];
                setDevices(deviceData);

                // Create options for dropdown - value is deviceId, label shows deviceCode
                const options = deviceData.map(device => ({
                    value: device._id,
                    label: `${device.deviceCode} - ${device.deviceName || ''} (${device.deviceType || ''})`
                }));
                setDeviceOptions(options);
            }
        }).catch(error => {
            console.error('Error fetching devices:', error);
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Update the form state when inputs change
        dispatch(setApiJson({
            ...ApiReducer.apiJson,
            [name]: value
        }));
    };

    const handleSelectChange = (name, value) => {
        // Update the form state when select inputs change
        dispatch(setApiJson({
            ...ApiReducer.apiJson,
            [name]: value
        }));
    };

    const handleSwitchChange = (name, checked) => {
        // Update the form state when switch toggles change
        dispatch(setApiJson({
            ...ApiReducer.apiJson,
            [name]: checked
        }));
    };

    const handleSubmit = () => {
        // Set loading state
        setIsLoading(true);

        // Clear previous errors
        dispatch(setApiErrorJson({}));

        // Validate the form data without modifying it yet
        const formData = {
            ...ApiReducer.apiJson
        };

        BillboardValidation(formData).then((errors) => {
            console.log("Validation errors:", errors);
            dispatch(setApiErrorJson(errors));

            // If no errors, proceed with API call
            if (Object.keys(errors).length === 0) {
                const submissionData = {
                    ...formData,
                    isActive: Boolean(formData.isActive),
                    duration: parseFloat(formData.duration) || 10,
                    unitIds: getSelectedUnit()?._id
                };

                if (isEditMode) {
                    HitApi(submissionData, updateMedia).then((res) => {
                        console.log("Update response:", res);
                        setIsLoading(false);
                        if (res?.message === "Media updated successfully") {
                            window.location.pathname = '/billboard';
                        }
                    }).catch(error => {
                        console.error("Error updating billboard:", error);
                        setIsLoading(false);
                    });
                } else {
                    HitApi(submissionData, addMedia).then((res) => {
                        console.log("Create response:", res);
                        setIsLoading(false);
                        if (res?.statusCode === 201) {
                            window.location.pathname = '/billboard';
                        }
                    }).catch(error => {
                        console.error("Error creating billboard:", error);
                        setIsLoading(false);
                    });
                }
            } else {
                setIsLoading(false);
            }
        });
    };

    const loadData = () => {
        setIsLoading(true);

        var json = {
            page: 1,
            limit: 10,
            search: {
                _id: urlId
            }
        };

        HitApi(json, searchMedia).then((res) => {
            console.log("billboard result", res);
            if (res?.statusCode === 200) {
                const billboardData = res?.data?.docs?.[0] || res?.data?.[0];
                if (billboardData) {
                    // Process data for the form
                    const processedData = {
                        _id: billboardData._id || "",
                        duration: billboardData.duration ? billboardData.duration.toString() : "",
                        type: billboardData.type || "",
                        deviceId: billboardData.deviceId || "",
                        source: billboardData.source || "",
                        isActive: billboardData.isActive === true
                    };
                    dispatch(setApiJson(processedData));
                }
            }
            setIsLoading(false);
        }).catch(error => {
            console.error("Error fetching billboard data:", error);
            setIsLoading(false);
        });
    };

    // Helper function to check if a field has an error
    const hasError = (fieldName) => {
        return ApiReducer.apiJsonError && ApiReducer.apiJsonError[fieldName];
    };

    // Helper function to get error message
    const getErrorMsg = (fieldName) => {
        return ApiReducer.apiJsonError ? ApiReducer.apiJsonError[fieldName] : "";
    };

    // Helper function to safely get form values with fallbacks
    const getFormValue = (fieldName, defaultValue = "") => {
        return ApiReducer.apiJson && ApiReducer.apiJson[fieldName] !== undefined
            ? ApiReducer.apiJson[fieldName]
            : defaultValue;
    };

    return (
        <div className="p-5">
            <PageHeader
                title={isEditMode ? 'Edit Billboard' : 'Add New Billboard'}
                description={isEditMode ? 'Update Your Billboard Advertisement' : 'Create a New Billboard Advertisement'}
            />
            <Box className="bg-white p-8 border rounded-xl mb-6">
                <div>
                    <div className="text-lg font-medium mb-5 flex justify-between items-center">
                        <span>Advertisement Details</span>

                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <AppInput
                        title="Duration (seconds)"
                        placeholder="Enter Duration"
                        name="duration"
                        important={true}
                        error={hasError('duration')}
                        errormsg={getErrorMsg('duration')}
                        value={getFormValue('duration')}
                        onChange={handleInputChange}
                    />

                    <AppSelect
                        title="File Type"
                        name="type"
                        options={typeOptions}
                        important={true}
                        error={hasError('type')}
                        errormsg={getErrorMsg('type')}
                        placeholder="Select File"
                        value={getFormValue('type')}
                        onChange={(value) => handleSelectChange('type', value)}
                    />

                    <AppSelect
                        title="Device Code"
                        name="deviceId"
                        options={deviceOptions}
                        important={true}
                        error={hasError('deviceId')}
                        errormsg={getErrorMsg('deviceId')}
                        placeholder="Select Device"
                        value={getFormValue('deviceId')}
                        onChange={(value) => handleSelectChange('deviceId', value)}
                    />

                    <div className='flex flex-col justify-center'>
                        <AppSwitch
                            title="Is Active"
                            name="isActive"
                            specification="Toggle to enable or disable this advertisement"
                            error={hasError('isActive')}
                            errormsg={getErrorMsg('isActive')}
                            checked={getFormValue('isActive', false)}
                            onChange={(checked) => handleSwitchChange('isActive', checked)}
                        />
                    </div>
                </div>

                <FileuploadComp
                    title="Product Image URL"
                    name="source"
                    allowed={["img", "gif", "video"]}
                    error={hasError('source')}
                    errormsg={getErrorMsg('source')}
                // disabled={isSubmitting}
                />

                {isEditMode && getFormValue('source') && (
                    <div className="mt-4 mb-6">
                        <p className="text-sm font-medium mb-2">Preview:</p>
                        <div className="border rounded-lg overflow-hidden max-w-md">
                            {getFormValue('type') === 'video' ? (
                                <video
                                    src={getFormValue('source')}
                                    controls
                                    className="max-w-full h-auto"
                                    style={{ maxHeight: '200px' }}
                                />
                            ) : (
                                <img
                                    src={getFormValue('source')}
                                    alt="Billboard Preview"
                                    className="max-w-full h-auto"
                                    style={{ maxHeight: '200px' }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/placeholder-image.png';
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}
                <div className='mt-4 flex justify-end'>
                    <AppButton
                        buttontext={isEditMode ? "Update Billboard" : "Create Billboard"}
                        onClick={handleSubmit}
                        disabled={isLoading}
                    />
                </div>

            </Box>


        </div>
    );
}

export default AddBillBoard;